import type { HoursAdjustChecklist } from './annualTimetableHoursAdjustPlan';
import type {
  TrackHoursAdjustTrackSettings,
  TrackHoursTermScope,
} from './trackHoursTermScopeStorage';

/** 저장 시점 원본(상단) 테이블 스냅샷 — 편제별 */
export type HoursAdjustSourceTrackSnapshot = {
  trackName: string;
  termScope: TrackHoursTermScope;
  excludeFromAdjust: boolean;
  timetableNamesLabel: string;
  criteriaHours: number;
  operatingTerm1: number;
  operatingTerm2: number;
  fixedTerm1: number;
  fixedTerm2: number;
};

export type HoursAdjustModalSession = {
  version: 1;
  savedAt: string;
  settingsByTrack: Record<string, TrackHoursAdjustTrackSettings>;
  learningHoursByKey: Record<string, string>;
  sourceSnapshot: HoursAdjustSourceTrackSnapshot[];
  checklist: HoursAdjustChecklist | null;
  checklistChecked: Record<string, boolean>;
  /** 체크리스트 자동 반영 시 건드리지 않을 색(hex). 기본 미선택 = [] */
  applySkipColorHexes: string[];
};

function sessionKey(workspaceId: string, gradeId: string): string {
  return `cp.hoursAdjustModalSession.v1.${workspaceId}.${gradeId}`;
}

function isObject(v: unknown): v is Record<string, unknown> {
  return Boolean(v) && typeof v === 'object' && !Array.isArray(v);
}

function normalizeSession(raw: unknown): HoursAdjustModalSession | null {
  if (!isObject(raw) || raw.version !== 1) return null;
  if (!isObject(raw.settingsByTrack)) return null;
  if (!isObject(raw.learningHoursByKey)) return null;
  if (!Array.isArray(raw.sourceSnapshot)) return null;
  const learningHoursByKey: Record<string, string> = {};
  Object.entries(raw.learningHoursByKey).forEach(([k, v]) => {
    if (typeof v === 'string') learningHoursByKey[k] = v;
  });
  const checklistChecked: Record<string, boolean> = {};
  if (isObject(raw.checklistChecked)) {
    Object.entries(raw.checklistChecked).forEach(([k, v]) => {
      checklistChecked[k] = v === true;
    });
  }
  const applySkipColorHexes = Array.isArray(raw.applySkipColorHexes)
    ? raw.applySkipColorHexes.filter((v): v is string => typeof v === 'string' && v.trim() !== '')
    : [];
  return {
    version: 1,
    savedAt: typeof raw.savedAt === 'string' ? raw.savedAt : new Date().toISOString(),
    settingsByTrack: raw.settingsByTrack as Record<string, TrackHoursAdjustTrackSettings>,
    learningHoursByKey,
    sourceSnapshot: raw.sourceSnapshot as HoursAdjustSourceTrackSnapshot[],
    checklist: (raw.checklist as HoursAdjustChecklist | null) ?? null,
    checklistChecked,
    applySkipColorHexes,
  };
}

/** 모달 작업 세션 로드 (localStorage 캐시, 네트워크 없음) */
export function loadHoursAdjustModalSession(
  workspaceId: string,
  gradeId: string
): HoursAdjustModalSession | null {
  if (!workspaceId || !gradeId) return null;
  try {
    const raw = window.localStorage.getItem(sessionKey(workspaceId, gradeId));
    if (!raw) return null;
    return normalizeSession(JSON.parse(raw) as unknown);
  } catch {
    return null;
  }
}

/** 모달 작업 세션 저장 (localStorage 캐시). 1·2차 조정 결과는 저장하지 않음. */
export function saveHoursAdjustModalSession(
  workspaceId: string,
  gradeId: string,
  session: HoursAdjustModalSession
): void {
  if (!workspaceId || !gradeId) return;
  try {
    window.localStorage.setItem(sessionKey(workspaceId, gradeId), JSON.stringify(session));
  } catch {
    /* quota */
  }
}

/** 체크리스트·제외색 등만 패치 (기존 세션 유지, 없으면 무시) */
export function patchHoursAdjustModalSessionChecklist(
  workspaceId: string,
  gradeId: string,
  patch: {
    checklist?: HoursAdjustChecklist | null;
    checklistChecked?: Record<string, boolean>;
    applySkipColorHexes?: string[];
  }
): void {
  const prev = loadHoursAdjustModalSession(workspaceId, gradeId);
  if (!prev) return;
  saveHoursAdjustModalSession(workspaceId, gradeId, {
    ...prev,
    savedAt: new Date().toISOString(),
    checklist: patch.checklist !== undefined ? patch.checklist : prev.checklist,
    checklistChecked:
      patch.checklistChecked !== undefined ? patch.checklistChecked : prev.checklistChecked,
    applySkipColorHexes:
      patch.applySkipColorHexes !== undefined
        ? patch.applySkipColorHexes
        : prev.applySkipColorHexes,
  });
}

/** 초기화: 저장된 모달 세션 삭제 */
export function clearHoursAdjustModalSession(workspaceId: string, gradeId: string): void {
  if (!workspaceId || !gradeId) return;
  try {
    window.localStorage.removeItem(sessionKey(workspaceId, gradeId));
  } catch {
    /* ignore */
  }
}
